/**
 * TanStack Query hooks backing the admin dashboard page.
 *
 * Each hook wraps a single GET endpoint exposed under
 * `/api/v1/admin/dashboard/*` (plus the error-log stats endpoint which
 * lives under `/api/v1/admin/error-logs/stats`). Returning the raw `useQuery`
 * result keeps the page free to compose `isPending` / `isError` / `data`
 * however it likes (skeletons vs. inline error banners vs. retry).
 *
 * The error-log stats query is intentionally tolerant of a 4xx/5xx —
 * historically the endpoint can be flaky in some environments and the
 * legacy dashboard explicitly swallowed the failure
 * (`api.get(...).catch(() => null)`). We mirror that behaviour with
 * `retry: false` + `throwOnError: false` so the rest of the dashboard
 * still renders even when this single card has no data.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { merchantApiFetch } from '@favelafood/shared/api/fetcher';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';

type DashboardStats = components['schemas']['DashboardStatsResponse'];
type OrderResponse = components['schemas']['OrderResponse'];
type ErrorLogStats = components['schemas']['ErrorLogStatsResponse'];

/** Single row of `/dashboard/orders-by-hour`. The OpenAPI schema returns
 *  `Record<string, never>[]`, so we narrow to a local interface here. */
export interface OrdersByHourRow {
  hour: number;
  count: number;
}

/** Single row of `/dashboard/order-status-distribution`. Same as above. */
export interface StatusDistributionRow {
  status: string;
  count: number;
}

export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: () => [...dashboardKeys.all, 'stats'] as const,
  recentOrders: (limit: number) => [...dashboardKeys.all, 'recent-orders', limit] as const,
  ordersByHour: () => [...dashboardKeys.all, 'orders-by-hour'] as const,
  statusDistribution: () => [...dashboardKeys.all, 'status-distribution'] as const,
  errorStats: (hours: number) => [...dashboardKeys.all, 'error-stats', hours] as const,
};

const unwrap = <T,>(data: T | undefined, error: unknown): T => {
  if (error) throw error;
  if (data === undefined) {
    throw new Error('Resposta vazia do servidor');
  }
  return data;
};

export const useDashboardStats = (): UseQueryResult<DashboardStats> =>
  useQuery({
    queryKey: dashboardKeys.stats(),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/dashboard/stats');
      return unwrap(data, error);
    },
  });

export const useRecentOrders = (limit = 10): UseQueryResult<OrderResponse[]> =>
  useQuery({
    queryKey: dashboardKeys.recentOrders(limit),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/dashboard/recent-orders', {
        params: { query: { limit } },
      });
      return unwrap(data, error);
    },
  });

export const useOrdersByHour = (): UseQueryResult<OrdersByHourRow[]> =>
  useQuery({
    queryKey: dashboardKeys.ordersByHour(),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/dashboard/orders-by-hour');
      // OpenAPI types this as `Record<string, never>[]`; cast through unknown
      // because the server actually returns `{ hour, count }[]`.
      // TODO: regenerate types once backend specifies a richer schema.
      return unwrap(data, error) as unknown as OrdersByHourRow[];
    },
  });

export const useStatusDistribution = (): UseQueryResult<StatusDistributionRow[]> =>
  useQuery({
    queryKey: dashboardKeys.statusDistribution(),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/dashboard/order-status-distribution');
      // Same situation as `useOrdersByHour` — OpenAPI returns
      // `Record<string, never>[]` but the runtime shape is `{ status, count }[]`.
      // TODO: regenerate types once backend specifies a richer schema.
      return unwrap(data, error) as unknown as StatusDistributionRow[];
    },
  });

export const useErrorLogStats = (hours = 24): UseQueryResult<ErrorLogStats> =>
  useQuery({
    queryKey: dashboardKeys.errorStats(hours),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/admin/error-logs/stats', {
        params: { query: { hours } },
      });
      return unwrap(data, error);
    },
    // Endpoint can be flaky in dev environments — a failure here must not
    // poison the dashboard's overall isPending flag or trigger global retry.
    retry: false,
    throwOnError: false,
  });

/* ------------------------------------------------------------------ */
/* Métricas de REDE (crescimento + cobertura).                         */
/* Saíram do painel do staff — são métrica de rede, papel do admin. Os  */
/* endpoints vivem sob /api/v1/staff/dashboard/* mas já autorizam ADMIN */
/* (StaffDashboardController: hasAnyRole('STAFF','ADMIN')). Não estão   */
/* no OpenAPI gerado, então usamos merchantApiFetch direto.             */
/* ------------------------------------------------------------------ */

export interface GrowthPoint {
  weekStart: string;
  comerciantes: number;
  comercios: number;
}

export interface CoveragePoint {
  neighborhood: string;
  activeMerchants: number;
}

export const useNetworkGrowth = (): UseQueryResult<GrowthPoint[]> =>
  useQuery<GrowthPoint[]>({
    queryKey: ['admin', 'dashboard', 'network-growth'],
    queryFn: () => merchantApiFetch<GrowthPoint[]>('/api/v1/staff/dashboard/growth'),
    retry: false,
    throwOnError: false,
  });

export const useNetworkCoverage = (): UseQueryResult<CoveragePoint[]> =>
  useQuery<CoveragePoint[]>({
    queryKey: ['admin', 'dashboard', 'network-coverage'],
    queryFn: () => merchantApiFetch<CoveragePoint[]>('/api/v1/staff/dashboard/coverage'),
    retry: false,
    throwOnError: false,
  });
